
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from "lucide-react";
import { strings } from "../../strings/strings";


const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 5,
  onPageChange,
  showItemsPerPage = true,
  onItemsPerPageChange,
  itemsPerPageOptions = [12, 24, 36, 100],
  className = "",
}) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange?.(page);
    }
  };

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-2 select-none ${className}`}>
      {/* Mobile: Per Page + Total in one line */}
      <div className="w-full flex items-center justify-between sm:hidden">
        {/* Per Page */}
        {showItemsPerPage && onItemsPerPageChange && (
          <div className="relative flex items-center bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-200 hover:border-primary/50 focus-within:border-primary">
            <label className="pl-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {strings.perPage}
            </label>
            <div className="relative flex items-center pr-7">
              <select
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                className="pl-1.5 pr-1 py-1.5 bg-transparent text-xs font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer appearance-none"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2 w-3.5 h-3.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Total */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 dark:bg-primary/20 border border-primary/20 rounded-2xl shadow-sm">
          <span className="text-[10px] font-bold text-primary dark:text-primary/70 uppercase tracking-widest">
            {strings.total}
          </span>
          <span className="text-xs font-black text-primary-dark dark:text-primary">{totalItems}</span>
        </div>
      </div>

      {/* Desktop Left: Per Page */}
      <div className="hidden sm:flex items-center gap-4 order-2 sm:order-1">
        {showItemsPerPage && onItemsPerPageChange && (
          <div className="relative flex items-center bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-200 hover:border-primary/50 focus-within:border-primary">
            <label
              htmlFor="items-per-page"
              className="pl-3.5 text-xs font-bold text-gray-500 dark:text-gray-400 whitespace-nowrap uppercase tracking-wider"
            >
              {strings.perPage}
            </label>
            <div className="relative flex items-center pr-8">
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                className="pl-2 pr-1.5 py-1.5 bg-transparent text-sm font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer appearance-none"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2.5 w-4 h-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}
      </div>

      {/* Right side: Pagination + Total (Desktop) */}
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 order-1 sm:order-2 w-full sm:w-auto">
        {/* Pagination */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary hover:text-primary-dark dark:hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:scale-105 active:scale-95"
            aria-label={strings.previousPage}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-1.5">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-400 dark:text-gray-500 font-bold">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`min-w-[36px] h-9 px-3 flex items-center justify-center text-sm font-bold rounded-xl transition-all duration-200 ${
                    currentPage === page
                      ? "bg-gradient-brand text-white shadow-lg shadow-primary/30 scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary-dark dark:hover:text-primary shadow-sm hover:scale-105 active:scale-95"
                  }`}
                >
                  {page}
                </button>
              ),
            )}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary hover:text-primary-dark dark:hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:scale-105 active:scale-95"
            aria-label={strings.nextPage}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Total (Desktop only) */}
        <div className="hidden sm:flex items-center gap-2 px-4 py-1.5 bg-primary/10 dark:bg-primary/20 border border-primary/20 rounded-2xl shadow-sm">
          <span className="text-[10px] font-bold text-primary dark:text-primary/70 uppercase tracking-widest">
            {strings.total}
          </span>
          <span className="text-sm font-black text-primary-dark dark:text-primary">{totalItems}</span>
        </div>
      </div>
    </div>
  );
};

export default Pagination;